// Universal Query Parser — hybrid retrieval with exact/phrase/semantic matching

export interface ParsedQuery {
  raw: string;
  category: string;
  productType: string;
  budget?: { max: number; currency: string };
  urgency?: "tomorrow" | "fast" | "normal";
  country?: string;
  region?: string;
  priorities: string[];
  localPreference?: "local" | "international" | "adaptive";
  riskTolerance?: "low" | "medium" | "high";
  useCase?: string;
  retrieval: RetrievalResult;
}

// ─── Retrieval Types ─────────────────────────────────────────────────────────

export type RetrievalMode = "exact" | "phrase" | "semantic";

export interface RetrievalCandidate {
  productKey: string;
  matchedBrand?: string;
  matchedModel?: string;
  retrievalModes: RetrievalMode[];
  retrievalConfidence: number;
  categoryAlignment: boolean;
  budgetAlignment: boolean;
  useCaseAlignment: boolean;
  geographyAlignment: boolean;
  matchedAttributes: string[];
}

export interface RetrievalResult {
  candidates: RetrievalCandidate[];
  exactMatches: number;
  phraseMatches: number;
  semanticMatches: number;
  totalCandidatesBeforeFilter: number;
  filteredOut: number;
  auditSummary: string;
}

// ─── Brand / Model Database ──────────────────────────────────────────────────

interface BrandEntry {
  brand: string;
  models: string[];
  category: string;
  productType: string;
}

const BRAND_DATABASE: BrandEntry[] = [
  // Monitors
  { brand: "samsung", models: ["odyssey g7", "odyssey g9", "viewfinity s9", "smart monitor m8"], category: "electronics", productType: "monitor" },
  { brand: "lg", models: ["ultragear 27gp950", "ultragear 27gn950", "ultrawide 34wn80c", "ergo 27un880", "27up850"], category: "electronics", productType: "monitor" },
  { brand: "dell", models: ["ultrasharp u2723qe", "ultrasharp u3223qe", "s2722dgm", "alienware aw3423dwf"], category: "electronics", productType: "monitor" },
  { brand: "asus", models: ["proart pa278qv", "proart pa32ucg", "rog swift pg279qm", "tuf gaming vg27aq1a"], category: "electronics", productType: "monitor" },
  { brand: "gigabyte", models: ["m27q x", "m27q", "m32u", "aorus fi32u"], category: "electronics", productType: "monitor" },
  { brand: "benq", models: ["pd2725u", "pd3220u", "mobiuz ex2710q", "zowie xl2546k"], category: "electronics", productType: "monitor" },
  // Office Chairs
  { brand: "herman miller", models: ["aeron", "embody", "sayl", "cosm", "mirra 2"], category: "furniture", productType: "office chair" },
  { brand: "secretlab", models: ["titan evo", "titan evo 2022", "magnus desk"], category: "furniture", productType: "office chair" },
  { brand: "steelcase", models: ["leap v2", "gesture", "karman", "think"], category: "furniture", productType: "office chair" },
  { brand: "hon", models: ["ignition 2.0", "wave mesh", "nucleus"], category: "furniture", productType: "office chair" },
  { brand: "autonomous", models: ["ergochair pro", "ergochair plus", "ergochair core"], category: "furniture", productType: "office chair" },
  // Vacuums
  { brand: "dyson", models: ["v15 detect", "v15 detect absolute", "v12 detect slim", "v10", "v8", "gen5detect", "ball animal"], category: "appliances", productType: "vacuum" },
  { brand: "shark", models: ["navigator lift-away nv352", "navigator nv356e", "vertex az2002", "stratos az3002", "ionflex"], category: "appliances", productType: "vacuum" },
  { brand: "bissell", models: ["crosswave pet pro", "cleanview swivel", "iconpet turbo", "spotclean pro"], category: "appliances", productType: "vacuum" },
  { brand: "roborock", models: ["s8 pro ultra", "s7 maxv ultra", "q revo"], category: "appliances", productType: "vacuum" },
  { brand: "miele", models: ["triflex hx2", "complete c3", "blizzard cx1"], category: "appliances", productType: "vacuum" },
  // Headphones
  { brand: "sony", models: ["wh-1000xm5", "wh-1000xm4", "wf-1000xm5", "ult wear"], category: "audio", productType: "headphones" },
  { brand: "apple", models: ["airpods max", "airpods pro 2", "airpods 4"], category: "audio", productType: "headphones" },
  { brand: "bose", models: ["quietcomfort ultra", "quietcomfort 45", "700"], category: "audio", productType: "headphones" },
  { brand: "soundcore", models: ["space q45", "space one", "liberty 4 nc"], category: "audio", productType: "earbuds" },
  // Espresso
  { brand: "breville", models: ["barista express", "barista pro", "bambino plus", "oracle touch"], category: "appliances", productType: "espresso machine" },
  { brand: "de'longhi", models: ["stilosa", "dedica", "magnifica s", "la specialista"], category: "appliances", productType: "espresso machine" },
  { brand: "jura", models: ["e8", "e6", "z10", "ena 8"], category: "appliances", productType: "espresso machine" },
  // Protein
  { brand: "optimum nutrition", models: ["gold standard whey", "gold standard casein", "serious mass"], category: "health", productType: "protein powder" },
  { brand: "myprotein", models: ["impact whey", "clear whey isolate", "vegan blend"], category: "health", productType: "protein powder" },
  { brand: "transparent labs", models: ["100% grass-fed whey", "organic vegan", "mass gainer"], category: "health", productType: "protein powder" },
  // Air Fryers
  { brand: "ninja", models: ["foodi dualzone", "air fryer max xl", "foodi digital"], category: "appliances", productType: "air fryer" },
  { brand: "cosori", models: ["pro le", "lite", "dual blaze"], category: "appliances", productType: "air fryer" },
  { brand: "instant", models: ["vortex plus 6-in-1", "vortex mini", "vortex pro"], category: "appliances", productType: "air fryer" },
  // Skincare
  { brand: "nuface", models: ["trinity+", "mini+", "fix"], category: "health", productType: "skincare device" },
  { brand: "foreo", models: ["luna 4", "luna 3", "ufo 2"], category: "health", productType: "skincare device" },
];

// ─── Phrase Patterns ─────────────────────────────────────────────────────────

interface PhrasePattern {
  pattern: RegExp;
  productType: string;
  category: string;
  extractedAttributes: string[];
  intentStrength: number;
}

const PHRASE_PATTERNS: PhrasePattern[] = [
  // Office chairs
  { pattern: /(?:office|desk|ergonomic)\s*chair\s*(?:for|with)\s*(?:back\s*pain|lumbar|support)/i, productType: "office chair", category: "furniture", extractedAttributes: ["ergonomics", "lumbar support", "back pain relief"], intentStrength: 0.9 },
  { pattern: /(?:office|desk)\s*chair\s*under\s*\d+/i, productType: "office chair", category: "furniture", extractedAttributes: ["budget-conscious"], intentStrength: 0.85 },
  { pattern: /(?:best|good|comfortable)\s*(?:office|desk|work)\s*chair/i, productType: "office chair", category: "furniture", extractedAttributes: ["comfort", "quality"], intentStrength: 0.8 },
  // Monitors
  { pattern: /monitor\s*(?:for|under)\s*(?:\d+|editing|gaming|coding|work)/i, productType: "monitor", category: "electronics", extractedAttributes: ["use-case-specific"], intentStrength: 0.85 },
  { pattern: /(?:4k|1440p|ultrawide|curved)\s*monitor/i, productType: "monitor", category: "electronics", extractedAttributes: ["spec-driven"], intentStrength: 0.85 },
  { pattern: /(?:best|good)\s*monitor\s*(?:for|under)/i, productType: "monitor", category: "electronics", extractedAttributes: ["quality"], intentStrength: 0.8 },
  // Vacuums
  { pattern: /(?:quiet|silent)\s*vacuum\s*(?:for|cleaner)/i, productType: "vacuum", category: "appliances", extractedAttributes: ["low noise"], intentStrength: 0.85 },
  { pattern: /vacuum\s*(?:for|with)\s*pet\s*hair/i, productType: "vacuum", category: "appliances", extractedAttributes: ["pet hair handling"], intentStrength: 0.9 },
  { pattern: /(?:cordless|stick)\s*vacuum/i, productType: "vacuum", category: "appliances", extractedAttributes: ["cordless", "portability"], intentStrength: 0.8 },
  { pattern: /(?:best|good|powerful)\s*vacuum\s*(?:cleaner)?/i, productType: "vacuum", category: "appliances", extractedAttributes: ["quality", "power"], intentStrength: 0.75 },
  // Headphones
  { pattern: /(?:noise\s*cancell?ing|anc)\s*(?:headphones|earbuds|over[\s-]?ear)/i, productType: "headphones", category: "audio", extractedAttributes: ["noise cancellation"], intentStrength: 0.9 },
  { pattern: /(?:wireless|bluetooth)\s*(?:headphones|earbuds)/i, productType: "headphones", category: "audio", extractedAttributes: ["wireless"], intentStrength: 0.75 },
  // Espresso
  { pattern: /espresso\s*machine\s*(?:for|under|with)/i, productType: "espresso machine", category: "appliances", extractedAttributes: ["use-case-specific"], intentStrength: 0.85 },
  { pattern: /(?:best|good)\s*(?:espresso|coffee)\s*(?:machine|maker)/i, productType: "espresso machine", category: "appliances", extractedAttributes: ["quality"], intentStrength: 0.8 },
  // Protein
  { pattern: /(?:best|good|cleanest)\s*(?:protein|whey|casein)\s*(?:powder)?/i, productType: "protein powder", category: "health", extractedAttributes: ["quality"], intentStrength: 0.8 },
  { pattern: /(?:vegan|plant[\s-]?based)\s*protein/i, productType: "protein powder", category: "health", extractedAttributes: ["plant-based", "vegan"], intentStrength: 0.85 },
  // Air fryer
  { pattern: /(?:best|good)\s*air\s*fryer/i, productType: "air fryer", category: "appliances", extractedAttributes: ["quality"], intentStrength: 0.8 },
  { pattern: /air\s*fryer\s*(?:for|under)\s*(?:\d+|family|large)/i, productType: "air fryer", category: "appliances", extractedAttributes: ["use-case-specific"], intentStrength: 0.85 },
  // Skincare
  { pattern: /(?:best|good)\s*(?:skincare|facial)\s*(?:device|tool)/i, productType: "skincare device", category: "health", extractedAttributes: ["quality"], intentStrength: 0.8 },
];

// ─── Semantic Concept Map ────────────────────────────────────────────────────

interface SemanticMapping {
  triggers: string[];
  productType: string;
  category: string;
  attributes: string[];
}

const SEMANTIC_MAP: SemanticMapping[] = [
  { triggers: ["back pain", "spine", "posture", "sitting all day", "hurts to sit"], productType: "office chair", category: "furniture", attributes: ["ergonomics", "lumbar support"] },
  { triggers: ["won't hurt my back", "comfortable to sit", "long hours sitting"], productType: "office chair", category: "furniture", attributes: ["comfort", "ergonomics"] },
  { triggers: ["color accurate", "photo editing", "video editing", "design work", "creative work"], productType: "monitor", category: "electronics", attributes: ["color accuracy", "professional use"] },
  { triggers: ["gaming monitor", "high refresh", "fast response", "competitive gaming"], productType: "monitor", category: "electronics", attributes: ["gaming performance", "refresh rate"] },
  { triggers: ["coding monitor", "programming", "developer", "text clarity"], productType: "monitor", category: "electronics", attributes: ["text clarity", "productivity"] },
  { triggers: ["pet fur", "dog hair", "cat hair", "shedding", "pet mess"], productType: "vacuum", category: "appliances", attributes: ["pet hair handling"] },
  { triggers: ["quiet apartment", "won't wake", "noise level", "not loud", "condo"], productType: "vacuum", category: "appliances", attributes: ["low noise", "compact"] },
  { triggers: ["real espresso", "not pod", "not nespresso", "proper coffee", "barista"], productType: "espresso machine", category: "appliances", attributes: ["manual brewing", "quality"] },
  { triggers: ["latte", "cappuccino", "milk froth", "cafe at home"], productType: "espresso machine", category: "appliances", attributes: ["milk frothing", "versatility"] },
  { triggers: ["block noise", "airplane", "commute", "focus music", "open office"], productType: "headphones", category: "audio", attributes: ["noise cancellation"] },
  { triggers: ["workout headphones", "running earbuds", "gym", "sweat proof"], productType: "headphones", category: "audio", attributes: ["sport", "sweat resistance"] },
  { triggers: ["muscle building", "post workout", "gains", "bulk", "recovery"], productType: "protein powder", category: "health", attributes: ["muscle building"] },
  { triggers: ["healthy cooking", "oil free", "low fat cooking", "crispy"], productType: "air fryer", category: "appliances", attributes: ["healthy cooking"] },
  { triggers: ["anti aging", "wrinkle", "fine lines", "skin tightening"], productType: "skincare device", category: "health", attributes: ["anti-aging"] },
  { triggers: ["acne", "breakout", "pimple", "blemish", "clear skin"], productType: "skincare device", category: "health", attributes: ["acne treatment"] },
];

// ─── Use Case Detection ──────────────────────────────────────────────────────

const USE_CASE_PATTERNS: Record<string, string[]> = {
  gaming: ["gaming", "game", "fps", "competitive", "esports"],
  work: ["work", "office", "productivity", "business", "coding", "programming", "developer"],
  creative: ["creative", "editing", "photo", "video", "design", "color accurate", "content creation"],
  home: ["home", "apartment", "house", "family", "kids"],
  travel: ["travel", "commute", "airplane", "portable", "on the go"],
  fitness: ["gym", "workout", "exercise", "running", "fitness"],
  cooking: ["cooking", "kitchen", "meal prep", "family meals"],
};

function detectUseCase(lower: string): string | undefined {
  for (const [useCase, triggers] of Object.entries(USE_CASE_PATTERNS)) {
    if (triggers.some(t => lower.includes(t))) return useCase;
  }
  return undefined;
}

// ─── Category Map ────────────────────────────────────────────────────────────

const CATEGORY_MAP: Record<string, { category: string; productType: string }> = {
  "office chair": { category: "furniture", productType: "office chair" },
  "desk chair": { category: "furniture", productType: "office chair" },
  "standing desk": { category: "furniture", productType: "standing desk" },
  desk: { category: "furniture", productType: "desk" },
  monitor: { category: "electronics", productType: "monitor" },
  laptop: { category: "electronics", productType: "laptop" },
  keyboard: { category: "electronics", productType: "keyboard" },
  mouse: { category: "electronics", productType: "mouse" },
  tablet: { category: "electronics", productType: "tablet" },
  phone: { category: "electronics", productType: "phone" },
  smartphone: { category: "electronics", productType: "phone" },
  tv: { category: "electronics", productType: "tv" },
  television: { category: "electronics", productType: "tv" },
  speaker: { category: "electronics", productType: "speaker" },
  camera: { category: "electronics", productType: "camera" },
  webcam: { category: "electronics", productType: "webcam" },
  headphone: { category: "audio", productType: "headphones" },
  headphones: { category: "audio", productType: "headphones" },
  earbuds: { category: "audio", productType: "earbuds" },
  earphone: { category: "audio", productType: "earbuds" },
  vacuum: { category: "appliances", productType: "vacuum" },
  "vacuum cleaner": { category: "appliances", productType: "vacuum" },
  "air purifier": { category: "appliances", productType: "air purifier" },
  "air fryer": { category: "appliances", productType: "air fryer" },
  blender: { category: "appliances", productType: "blender" },
  "coffee maker": { category: "appliances", productType: "coffee maker" },
  "espresso machine": { category: "appliances", productType: "espresso machine" },
  microwave: { category: "appliances", productType: "microwave" },
  toaster: { category: "appliances", productType: "toaster" },
  dishwasher: { category: "appliances", productType: "dishwasher" },
  "washing machine": { category: "appliances", productType: "washing machine" },
  dryer: { category: "appliances", productType: "dryer" },
  refrigerator: { category: "appliances", productType: "refrigerator" },
  fridge: { category: "appliances", productType: "refrigerator" },
  "protein powder": { category: "health", productType: "protein powder" },
  "whey protein": { category: "health", productType: "protein powder" },
  "protein shake": { category: "health", productType: "protein powder" },
  supplement: { category: "health", productType: "supplement" },
  vitamins: { category: "health", productType: "vitamins" },
  "skincare device": { category: "health", productType: "skincare device" },
  "fitness tracker": { category: "health", productType: "fitness tracker" },
  smartwatch: { category: "health", productType: "smartwatch" },
  treadmill: { category: "fitness", productType: "treadmill" },
  "exercise bike": { category: "fitness", productType: "exercise bike" },
  "yoga mat": { category: "fitness", productType: "yoga mat" },
  mattress: { category: "home", productType: "mattress" },
  pillow: { category: "home", productType: "pillow" },
  "robot vacuum": { category: "home", productType: "robot vacuum" },
  "air conditioner": { category: "home", productType: "air conditioner" },
  "gaming chair": { category: "gaming", productType: "gaming chair" },
  "gaming mouse": { category: "gaming", productType: "gaming mouse" },
  "gaming keyboard": { category: "gaming", productType: "gaming keyboard" },
  "gaming headset": { category: "gaming", productType: "gaming headset" },
  console: { category: "gaming", productType: "console" },
  // ─── New categories ────────────────────────────────────────────────
  // Beauty & Skincare
  moisturizer: { category: "beauty", productType: "skincare" },
  serum: { category: "beauty", productType: "skincare" },
  cleanser: { category: "beauty", productType: "skincare" },
  sunscreen: { category: "beauty", productType: "skincare" },
  "face wash": { category: "beauty", productType: "skincare" },
  makeup: { category: "beauty", productType: "makeup" },
  foundation: { category: "beauty", productType: "makeup" },
  mascara: { category: "beauty", productType: "makeup" },
  perfume: { category: "beauty", productType: "fragrance" },
  cologne: { category: "beauty", productType: "fragrance" },
  shampoo: { category: "beauty", productType: "hair care" },
  conditioner: { category: "beauty", productType: "hair care" },
  "hair dryer": { category: "beauty", productType: "beauty tools" },
  // Pet
  "dog food": { category: "pet", productType: "dog food" },
  "cat food": { category: "pet", productType: "cat food" },
  "cat litter": { category: "pet", productType: "pet accessories" },
  "dog treats": { category: "pet", productType: "pet accessories" },
  "pet food": { category: "pet", productType: "dog food" },
  // Baby
  stroller: { category: "baby", productType: "strollers" },
  "baby stroller": { category: "baby", productType: "strollers" },
  "car seat": { category: "baby", productType: "car seats" },
  "baby car seat": { category: "baby", productType: "car seats" },
  diaper: { category: "baby", productType: "diapers" },
  diapers: { category: "baby", productType: "diapers" },
  "baby monitor": { category: "baby", productType: "baby monitors" },
  // Tools
  drill: { category: "tools", productType: "power tools" },
  "impact driver": { category: "tools", productType: "power tools" },
  "circular saw": { category: "tools", productType: "power tools" },
  "lawn mower": { category: "tools", productType: "garden tools" },
  // Sports & Fitness
  "running shoes": { category: "sports", productType: "running shoes" },
  sneakers: { category: "fashion", productType: "shoes" },
  dumbbells: { category: "sports", productType: "fitness equipment" },
  tent: { category: "sports", productType: "camping" },
  // Travel
  luggage: { category: "travel", productType: "luggage" },
  suitcase: { category: "travel", productType: "luggage" },
  backpack: { category: "travel", productType: "backpacks" },
  // Groceries
  "coffee beans": { category: "groceries", productType: "coffee" },
  detergent: { category: "groceries", productType: "laundry" },
  // Scale-Up: expanded category map
  "face cream": { category: "beauty", productType: "skincare" },
  "eye cream": { category: "beauty", productType: "skincare" },
  "anti-aging": { category: "beauty", productType: "skincare" },
  "night cream": { category: "beauty", productType: "skincare" },
  "hair care": { category: "beauty", productType: "hair care" },
  "hair mask": { category: "beauty", productType: "hair care" },
  "dry shampoo": { category: "beauty", productType: "hair care" },
  "lip gloss": { category: "beauty", productType: "makeup" },
  "setting spray": { category: "beauty", productType: "makeup" },
  "curling iron": { category: "beauty", productType: "beauty tools" },
  fragrance: { category: "beauty", productType: "fragrance" },
  "dog toys": { category: "pet", productType: "pet accessories" },
  "cat tree": { category: "pet", productType: "pet accessories" },
  "dog bed": { category: "pet", productType: "pet accessories" },
  "cat toys": { category: "pet", productType: "pet accessories" },
  "baby carrier": { category: "baby", productType: "baby gear" },
  "high chair": { category: "baby", productType: "baby gear" },
  "baby bottle": { category: "baby", productType: "baby gear" },
  "baby formula": { category: "baby", productType: "baby gear" },
  "socket set": { category: "tools", productType: "hand tools" },
  hammer: { category: "tools", productType: "hand tools" },
  pliers: { category: "tools", productType: "hand tools" },
  wrench: { category: "tools", productType: "hand tools" },
  "garden hose": { category: "tools", productType: "garden tools" },
  "dish soap": { category: "groceries", productType: "household cleaners" },
  bleach: { category: "groceries", productType: "household cleaners" },
  "all-purpose cleaner": { category: "groceries", productType: "household cleaners" },
  "laundry detergent": { category: "groceries", productType: "laundry" },
  "fabric softener": { category: "groceries", productType: "laundry" },
  "dryer sheets": { category: "groceries", productType: "laundry" },
  "stain remover": { category: "groceries", productType: "laundry" },
};

const COUNTRY_MAP: Record<string, { name: string; code: string; currency: string }> = {
  us: { name: "United States", code: "US", currency: "USD" },
  usa: { name: "United States", code: "US", currency: "USD" },
  "united states": { name: "United States", code: "US", currency: "USD" },
  america: { name: "United States", code: "US", currency: "USD" },
  uk: { name: "United Kingdom", code: "GB", currency: "GBP" },
  "united kingdom": { name: "United Kingdom", code: "GB", currency: "GBP" },
  britain: { name: "United Kingdom", code: "GB", currency: "GBP" },
  england: { name: "United Kingdom", code: "GB", currency: "GBP" },
  canada: { name: "Canada", code: "CA", currency: "CAD" },
  australia: { name: "Australia", code: "AU", currency: "AUD" },
  germany: { name: "Germany", code: "DE", currency: "EUR" },
  france: { name: "France", code: "FR", currency: "EUR" },
  japan: { name: "Japan", code: "JP", currency: "JPY" },
  uae: { name: "UAE", code: "AE", currency: "AED" },
  dubai: { name: "UAE", code: "AE", currency: "AED" },
  india: { name: "India", code: "IN", currency: "INR" },
  singapore: { name: "Singapore", code: "SG", currency: "SGD" },
  netherlands: { name: "Netherlands", code: "NL", currency: "EUR" },
  sweden: { name: "Sweden", code: "SE", currency: "SEK" },
  spain: { name: "Spain", code: "ES", currency: "EUR" },
  italy: { name: "Italy", code: "IT", currency: "EUR" },
  brazil: { name: "Brazil", code: "BR", currency: "BRL" },
  mexico: { name: "Mexico", code: "MX", currency: "MXN" },
  "south korea": { name: "South Korea", code: "KR", currency: "KRW" },
  korea: { name: "South Korea", code: "KR", currency: "KRW" },
  "new zealand": { name: "New Zealand", code: "NZ", currency: "NZD" },
  "saudi arabia": { name: "Saudi Arabia", code: "SA", currency: "SAR" },
  qatar: { name: "Qatar", code: "QA", currency: "QAR" },
  switzerland: { name: "Switzerland", code: "CH", currency: "CHF" },
  norway: { name: "Norway", code: "NO", currency: "NOK" },
};

const CURRENCY_SYMBOLS: Record<string, string> = {
  $: "USD", "£": "GBP", "€": "EUR", "¥": "JPY",
  "A$": "AUD", "C$": "CAD", "₹": "INR", "AED": "AED",
  "R$": "BRL", "kr": "SEK", "CHF": "CHF",
};

const PRIORITY_KEYWORDS: Record<string, string> = {
  "pet hair": "pet hair handling",
  quiet: "low noise",
  silent: "low noise",
  portable: "portability",
  lightweight: "portability",
  ergonomic: "ergonomics",
  comfort: "comfort",
  compact: "compact size",
  small: "compact size",
  wireless: "wireless",
  cordless: "wireless",
  "noise cancel": "noise cancellation",
  "noise-cancel": "noise cancellation",
  anc: "noise cancellation",
  gaming: "gaming performance",
  creative: "color accuracy",
  power: "power/suction",
  battery: "battery life",
  "fast charging": "fast charging",
  waterproof: "water resistance",
  "water resistant": "water resistance",
  organic: "organic/natural",
  vegan: "vegan",
  "whey isolate": "whey isolate",
  "plant based": "plant-based",
  acne: "acne treatment",
  durable: "durability",
  reliable: "reliability",
  "long lasting": "durability",
  premium: "premium quality",
  professional: "professional grade",
  beginner: "beginner friendly",
  "easy to use": "ease of use",
};

// ─── Hybrid Retrieval Engine ─────────────────────────────────────────────────

function runExactRetrieval(lower: string): RetrievalCandidate[] {
  const candidates: RetrievalCandidate[] = [];

  for (const entry of BRAND_DATABASE) {
    const brandFound = lower.includes(entry.brand);

    for (const model of entry.models) {
      const modelFound = lower.includes(model);

      if (brandFound && modelFound) {
        candidates.push({
          productKey: `${entry.brand}:${model}`,
          matchedBrand: entry.brand,
          matchedModel: model,
          retrievalModes: ["exact"],
          retrievalConfidence: 0.95,
          categoryAlignment: true,
          budgetAlignment: true,
          useCaseAlignment: true,
          geographyAlignment: true,
          matchedAttributes: ["brand", "model"],
        });
      } else if (modelFound) {
        candidates.push({
          productKey: `${entry.brand}:${model}`,
          matchedBrand: entry.brand,
          matchedModel: model,
          retrievalModes: ["exact"],
          retrievalConfidence: 0.85,
          categoryAlignment: true,
          budgetAlignment: true,
          useCaseAlignment: true,
          geographyAlignment: true,
          matchedAttributes: ["model"],
        });
      } else if (brandFound) {
        candidates.push({
          productKey: `${entry.brand}:${model}`,
          matchedBrand: entry.brand,
          retrievalModes: ["exact"],
          retrievalConfidence: 0.6,
          categoryAlignment: true,
          budgetAlignment: true,
          useCaseAlignment: true,
          geographyAlignment: true,
          matchedAttributes: ["brand"],
        });
      }
    }
  }

  return candidates;
}

function runPhraseRetrieval(lower: string): RetrievalCandidate[] {
  const candidates: RetrievalCandidate[] = [];

  for (const pp of PHRASE_PATTERNS) {
    if (pp.pattern.test(lower)) {
      const matching = BRAND_DATABASE.filter(b => b.productType === pp.productType);
      for (const entry of matching) {
        for (const model of entry.models) {
          candidates.push({
            productKey: `${entry.brand}:${model}`,
            matchedBrand: entry.brand,
            matchedModel: model,
            retrievalModes: ["phrase"],
            retrievalConfidence: pp.intentStrength * 0.85,
            categoryAlignment: true,
            budgetAlignment: true,
            useCaseAlignment: true,
            geographyAlignment: true,
            matchedAttributes: pp.extractedAttributes,
          });
        }
      }
    }
  }

  return candidates;
}

function runSemanticRetrieval(lower: string): RetrievalCandidate[] {
  const candidates: RetrievalCandidate[] = [];

  for (const sm of SEMANTIC_MAP) {
    const triggerHits = sm.triggers.filter(t => lower.includes(t));
    if (triggerHits.length === 0) continue;

    const semanticStrength = Math.min(triggerHits.length * 0.3, 0.7);
    const matching = BRAND_DATABASE.filter(b => b.productType === sm.productType);

    for (const entry of matching) {
      for (const model of entry.models) {
        candidates.push({
          productKey: `${entry.brand}:${model}`,
          matchedBrand: entry.brand,
          matchedModel: model,
          retrievalModes: ["semantic"],
          retrievalConfidence: semanticStrength,
          categoryAlignment: true,
          budgetAlignment: true,
          useCaseAlignment: true,
          geographyAlignment: true,
          matchedAttributes: sm.attributes,
        });
      }
    }
  }

  return candidates;
}

function mergeCandidates(
  exact: RetrievalCandidate[],
  phrase: RetrievalCandidate[],
  semantic: RetrievalCandidate[]
): RetrievalCandidate[] {
  const merged = new Map<string, RetrievalCandidate>();

  const addAll = (list: RetrievalCandidate[]) => {
    for (const c of list) {
      const existing = merged.get(c.productKey);
      if (existing) {
        const modes = new Set([...existing.retrievalModes, ...c.retrievalModes]);
        const modeCount = modes.size;
        const reinforcementBoost = 1 + (modeCount - 1) * 0.15;
        const baseConfidence = Math.max(existing.retrievalConfidence, c.retrievalConfidence);

        existing.retrievalModes = Array.from(modes) as RetrievalMode[];
        existing.retrievalConfidence = Math.min(baseConfidence * reinforcementBoost, 1.0);
        existing.matchedAttributes = [...new Set([...existing.matchedAttributes, ...c.matchedAttributes])];
      } else {
        merged.set(c.productKey, { ...c });
      }
    }
  };

  addAll(exact);
  addAll(phrase);
  addAll(semantic);

  return Array.from(merged.values());
}

function filterAndCapCandidates(
  candidates: RetrievalCandidate[],
  budget?: { max: number; currency: string },
  cap: number = 40
): { filtered: RetrievalCandidate[]; removedCount: number } {
  const before = candidates.length;

  let filtered = candidates.filter(c => {
    if (c.retrievalModes.length === 1 && c.retrievalModes[0] === "semantic" && c.retrievalConfidence < 0.25) {
      return false;
    }
    return true;
  });

  filtered.sort((a, b) => b.retrievalConfidence - a.retrievalConfidence);

  if (filtered.length > cap) {
    filtered = filtered.slice(0, cap);
  }

  return { filtered, removedCount: before - filtered.length };
}

function buildRetrievalAuditSummary(result: RetrievalResult): string {
  const parts: string[] = [];

  if (result.exactMatches > 0) {
    parts.push("High-confidence matches were found for your exact request");
  }
  if (result.phraseMatches > 0 && result.exactMatches === 0) {
    parts.push("Strong matches identified based on your specific requirements");
  }
  if (result.semanticMatches > 0 && result.exactMatches === 0 && result.phraseMatches === 0) {
    parts.push("Relevant alternatives identified from intelligent search");
  }
  if (result.semanticMatches > 0 && (result.exactMatches > 0 || result.phraseMatches > 0)) {
    parts.push("Expanded search included relevant alternatives beyond the most obvious matches");
  }
  if (result.filteredOut > 0) {
    parts.push(`${result.filteredOut} low-relevance or mismatched options were removed before scoring`);
  }

  return parts.join(". ") + ".";
}

// ─── Typo Correction ─────────────────────────────────────────────────────────

const TYPO_MAP: Record<string, string> = {
  moniter: "monitor", monior: "monitor", monitr: "monitor", mointor: "monitor",
  headphons: "headphones", headhpones: "headphones", hedphones: "headphones",
  vacum: "vacuum", vaccum: "vacuum", vacume: "vacuum", vaccuum: "vacuum",
  expresso: "espresso", esspresso: "espresso",
  keybord: "keyboard", keybaord: "keyboard", keybard: "keyboard",
  laptp: "laptop", laoptop: "laptop", latpop: "laptop",
  blutooth: "bluetooth", bluetooh: "bluetooth",
  erephone: "earphone", earbud: "earbuds",
  skinacre: "skincare", skincre: "skincare",
  protien: "protein", protine: "protein",
  arifryer: "air fryer", airfryr: "air fryer",
  tredmill: "treadmill", treadmil: "treadmill",
  refridgerator: "refrigerator", refridgator: "refrigerator",
  dishwashr: "dishwasher", diswasher: "dishwasher",
  microwve: "microwave", micowave: "microwave",
  smartwach: "smartwatch", smatwatch: "smartwatch",
};

function correctTypos(input: string): string {
  const words = input.split(/\s+/);
  return words.map(w => TYPO_MAP[w.toLowerCase()] || w).join(" ");
}

// ─── Main Parse Function ─────────────────────────────────────────────────────

export function parseQuery(raw: string, defaultContext?: { countryCode?: string; city?: string; country?: string; currency?: string }): ParsedQuery {
  const corrected = correctTypos(raw);
  const lower = corrected.toLowerCase().trim();

  // 1. Extract category
  let category = "general";
  let productType = "product";
  const sortedKeys = Object.keys(CATEGORY_MAP).sort((a, b) => b.length - a.length);
  for (const key of sortedKeys) {
    if (lower.includes(key)) {
      ({ category, productType } = CATEGORY_MAP[key]);
      break;
    }
  }

  // 2. Extract budget (expanded phrasing)
  let budget: ParsedQuery["budget"] | undefined;
  const budgetMatch = lower.match(/(?:under|below|max|up to|less than|budget|around|about|~)\s*([£€$₹]|[AC]\$)?\s*(\d[\d,]*)/);
  if (budgetMatch) {
    const symbol = budgetMatch[1] || "$";
    const amount = parseInt(budgetMatch[2].replace(/,/g, ""), 10);
    budget = { max: amount, currency: CURRENCY_SYMBOLS[symbol] || "USD" };
  } else {
    // "500ish", "500 or less", "max 500"
    const informalBudget = lower.match(/(\d[\d,]*)\s*(?:ish|or\s*less|or\s*under|maximum|max)/);
    if (informalBudget) {
      budget = { max: parseInt(informalBudget[1].replace(/,/g, ""), 10), currency: "USD" };
    } else {
      const rangeBudget = lower.match(/(\d[\d,]*)\s*[-–]\s*(\d[\d,]*)/);
      if (rangeBudget) {
        budget = { max: parseInt(rangeBudget[2].replace(/,/g, ""), 10), currency: "USD" };
      }
    }
  }

  // 3. Extract country
  let country: string | undefined;
  let region: string | undefined;
  let detectedCurrency: string | undefined;
  for (const [key, info] of Object.entries(COUNTRY_MAP)) {
    if (lower.includes(key)) {
      country = info.name;
      region = info.code;
      detectedCurrency = info.currency;
      break;
    }
  }

  if (budget && !country) {
    if (budget.currency === "GBP") { country = "United Kingdom"; region = "GB"; }
    else if (budget.currency === "EUR") { country = "Europe"; region = "EU"; }
    else if (budget.currency === "AUD") { country = "Australia"; region = "AU"; }
    else if (budget.currency === "CAD") { country = "Canada"; region = "CA"; }
    else if (budget.currency === "JPY") { country = "Japan"; region = "JP"; }
  }

  if (budget && detectedCurrency && budget.currency === "USD" && detectedCurrency !== "USD") {
    budget.currency = detectedCurrency;
  }

  // Apply default geo context if no country was extracted from query
  if (!country && defaultContext?.countryCode) {
    const codeToCountry: Record<string, { name: string; currency: string }> = {};
    for (const info of Object.values(COUNTRY_MAP)) {
      codeToCountry[info.code] = { name: info.name, currency: info.currency };
    }
    const match = codeToCountry[defaultContext.countryCode];
    if (match) {
      country = match.name;
      region = defaultContext.countryCode;
      if (budget && budget.currency === "USD" && match.currency !== "USD") {
        budget.currency = match.currency;
      }
    } else if (defaultContext.country) {
      country = defaultContext.country;
      region = defaultContext.countryCode;
    }
  }

  // 4. Extract urgency
  let urgency: ParsedQuery["urgency"];
  if (/tomorrow|next day|overnight/i.test(lower)) urgency = "tomorrow";
  else if (/fast|quick|rush|asap|urgent|same.day|delivered.*(?:today|tomorrow)/i.test(lower)) urgency = "fast";

  // 5. Extract priorities
  const priorities: string[] = [];
  for (const [keyword, priority] of Object.entries(PRIORITY_KEYWORDS)) {
    if (lower.includes(keyword) && !priorities.includes(priority)) {
      priorities.push(priority);
    }
  }

  // 6. Local preference
  let localPreference: ParsedQuery["localPreference"];
  if (/local|domestic|from here|in.country|nearby/i.test(lower)) localPreference = "local";
  else if (/international|global|cross.border|import|worldwide/i.test(lower)) localPreference = "international";

  // 7. Risk tolerance
  let riskTolerance: ParsedQuery["riskTolerance"];
  if (/safe|reliable|trusted|low.risk|warranty|guaranteed/i.test(lower)) riskTolerance = "low";
  else if (/cheap|cheapest|lowest|bargain/i.test(lower)) riskTolerance = "high";

  // 8. Use case detection
  const useCase = detectUseCase(lower);

  // ─── HYBRID RETRIEVAL ────────────────────────────────────────────────
  const exactCandidates = runExactRetrieval(lower);
  const phraseCandidates = runPhraseRetrieval(lower);
  const semanticCandidates = runSemanticRetrieval(lower);

  const allMerged = mergeCandidates(exactCandidates, phraseCandidates, semanticCandidates);
  const totalBeforeFilter = allMerged.length;

  const { filtered, removedCount } = filterAndCapCandidates(allMerged, budget);

  const retrieval: RetrievalResult = {
    candidates: filtered,
    exactMatches: exactCandidates.length,
    phraseMatches: phraseCandidates.length,
    semanticMatches: semanticCandidates.length,
    totalCandidatesBeforeFilter: totalBeforeFilter,
    filteredOut: removedCount,
    auditSummary: "",
  };
  retrieval.auditSummary = buildRetrievalAuditSummary(retrieval);

  // Override category/productType if retrieval found strong exact match
  if (exactCandidates.length > 0) {
    const topExact = exactCandidates.sort((a, b) => b.retrievalConfidence - a.retrievalConfidence)[0];
    const brandEntry = BRAND_DATABASE.find(b => b.brand === topExact.matchedBrand);
    if (brandEntry && topExact.retrievalConfidence >= 0.8) {
      category = brandEntry.category;
      productType = brandEntry.productType;
    }
  }

  return {
    raw,
    category,
    productType,
    budget,
    urgency,
    country,
    region,
    priorities,
    localPreference,
    riskTolerance,
    useCase,
    retrieval,
  };
}
